import { useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { debugLogger } from '@/utils/debugLogger';
import { useLikeLimits } from '@/hooks/useLikeLimits';
import { toast } from 'sonner';

export const useConnectionActions = () => {
  const { user } = useAuth();
  const { likeLimits, incrementLikeCount } = useLikeLimits();

  const deleteConnectionById = async (connectionId: string): Promise<boolean> => {
    if (!user) {
      debugLogger.error('useConnectionActions', 'User not authenticated for deleteConnectionById action');
      return false;
    }

    try {
      debugLogger.debug('useConnectionActions', 'Attempting to delete connection by ID', { 
        connectionId, 
        userId: user.id 
      });

      // Deletar conexão específica pelo ID
      const { error: deleteError, count } = await supabase
        .from('connections')
        .delete({ count: 'exact' })
        .eq('id', connectionId);

      if (deleteError) {
        debugLogger.error('useConnectionActions', 'Error deleting connection by ID', {
          connectionId,
          error: deleteError
        });
        return false;
      }

      debugLogger.debug('useConnectionActions', 'Connection delete by ID result', {
        connectionId,
        deletedRows: count
      });

      if (!count || count === 0) {
        debugLogger.debug('useConnectionActions', 'No connection was deleted', { connectionId });
        return false;
      }

      // Verificar se a conexão foi realmente deletada
      await new Promise(resolve => setTimeout(resolve, 100));

      const { data: verificationCheck, error: verifyError } = await supabase
        .from('connections')
        .select('id')
        .eq('id', connectionId);

      if (verifyError) {
        debugLogger.error('useConnectionActions', 'Error verifying deletion by ID', verifyError);
      } else {
        debugLogger.debug('useConnectionActions', 'Post-deletion verification by ID', {
          connectionId,
          stillExists: verificationCheck && verificationCheck.length > 0
        });
      }

      debugLogger.debug('useConnectionActions', 'Delete connection by ID completed successfully', { 
        connectionId, 
        userId: user.id
      });

      return true;
    } catch (error) {
      debugLogger.error('useConnectionActions', 'Unexpected error in deleteConnectionById', error);
      return false;
    }
  };

  const likeProfile = async (profileId: string): Promise<boolean> => {
    if (!user) {
      debugLogger.error('useConnectionActions', 'User not authenticated for like action');
      return false;
    }

    // Check daily like limits before proceeding
    if (!likeLimits.canLike) {
      const message = likeLimits.isUnlimited 
        ? 'Erro ao verificar limites de curtidas'
        : `Limite diário de curtidas atingido (${likeLimits.limit})`;
      
      toast.error(message);
      debugLogger.warn('useConnectionActions', 'Like blocked by daily limits', {
        current: likeLimits.current,
        limit: likeLimits.limit,
        canLike: likeLimits.canLike
      });
      return false;
    }

    // Validação adicional para evitar auto-conexões
    if (profileId === user.id) {
      debugLogger.error('useConnectionActions', 'Cannot like own profile', { profileId, userId: user.id });
      return false;
    }

    try {
      debugLogger.debug('useConnectionActions', 'Attempting to like profile', { 
        profileId, 
        userId: user.id 
      });

      // Verificar se já existe QUALQUER conexão entre os dois usuários (em qualquer direção)
      const { data: existingConnections, error: checkError } = await supabase
        .from('connections')
        .select('*')
        .or(`and(user_id.eq.${user.id},target_id.eq.${profileId}),and(user_id.eq.${profileId},target_id.eq.${user.id})`);

      if (checkError) {
        debugLogger.error('useConnectionActions', 'Error checking existing connections', checkError);
        return false;
      }

      debugLogger.debug('useConnectionActions', 'Existing connections found', { 
        count: existingConnections?.length || 0,
        connections: existingConnections 
      });

      // Analisar conexões existentes
      const directConnection = existingConnections?.find(conn => 
        conn.user_id === user.id && conn.target_id === profileId
      );
      
      const reverseConnection = existingConnections?.find(conn => 
        conn.user_id === profileId && conn.target_id === user.id
      );

      debugLogger.debug('useConnectionActions', 'Connection analysis', {
        directConnection: !!directConnection,
        directConnectionStatus: directConnection?.status,
        reverseConnection: !!reverseConnection,
        reverseConnectionStatus: reverseConnection?.status,
        reverseConnectionId: reverseConnection?.id
      });

      // Se já existe conexão direta, não pode curtir novamente
      if (directConnection) {
        debugLogger.info('useConnectionActions', 'Direct connection already exists', directConnection);
        return false;
      }

      // Se existe conexão reversa com status "sent", é um MATCH!
      if (reverseConnection && reverseConnection.status === 'sent') {
        debugLogger.debug('useConnectionActions', 'MATCH DETECTED! Updating existing connection to matched', {
          reverseConnectionId: reverseConnection.id,
          fromUserId: reverseConnection.user_id,
          toUserId: reverseConnection.target_id
        });

        // Atualizar a conexão existente para "matched"
        const { data: updatedConnection, error: updateError } = await supabase
          .from('connections')
          .update({ 
            status: 'matched'
            // Não precisamos alterar initiated_by pois deve manter o iniciador original
          })
          .eq('id', reverseConnection.id)
          .select()
          .single();

        if (updateError) {
          debugLogger.error('useConnectionActions', 'Error updating connection to matched', updateError);
          return false;
        }

        debugLogger.debug('useConnectionActions', 'MATCH CREATED SUCCESSFULLY!', {
          connectionId: updatedConnection.id,
          status: updatedConnection.status,
          initiatedBy: updatedConnection.initiated_by,
          action: 'MATCH_COMPLETED'
        });

        // Show success toast with match info - no like count needed for matches
        toast.success('🎉 É um Match!', {
          description: 'Vocês se curtiram mutuamente!'
        });

        return true;
      }

      // Se não há conexões existentes, criar nova conexão "sent"
      debugLogger.debug('useConnectionActions', 'Creating new connection', {
        fromUserId: user.id,
        toUserId: profileId,
        status: 'sent'
      });

      const { data: newConnection, error: insertError } = await supabase
        .from('connections')
        .insert({
          user_id: user.id,
          target_id: profileId,
          status: 'sent'
          // initiated_by será automaticamente definido como user.id pelo trigger
        })
        .select()
        .single();

      if (insertError) {
        debugLogger.error('useConnectionActions', 'Error creating connection', insertError);
        return false;
      }

      debugLogger.debug('useConnectionActions', 'New connection created successfully', {
        connectionId: newConnection.id,
        status: newConnection.status,
        initiatedBy: newConnection.initiated_by,
        action: 'LIKE_SENT'
      });

      // Increment like count after successful like
      incrementLikeCount();

      // Show success toast with remaining likes info
      const remainingAfterLike = likeLimits.remaining - 1;
      if (likeLimits.isUnlimited) {
        toast.success('Like enviado! ✨');
      } else {
        const remainingText = remainingAfterLike === 1 
          ? '1 curtida restante hoje'
          : `${remainingAfterLike} curtidas restantes hoje`;
        
        toast.success('Like enviado! ✨', {
          description: remainingText
        });
      }

      return true;
    } catch (error) {
      debugLogger.error('useConnectionActions', 'Unexpected error in likeProfile', error);
      return false;
    }
  };

  const unlikeProfile = async (profileId: string): Promise<boolean> => {
    if (!user) {
      debugLogger.error('useConnectionActions', 'User not authenticated for unlike action');
      return false;
    }

    try {
      debugLogger.debug('useConnectionActions', 'Attempting to unlike profile', { 
        profileId, 
        userId: user.id 
      });

      // Buscar TODAS as conexões entre os dois usuários em uma única query
      const { data: allConnections, error: searchError } = await supabase
        .from('connections')
        .select('*')
        .or(`and(user_id.eq.${user.id},target_id.eq.${profileId}),and(user_id.eq.${profileId},target_id.eq.${user.id})`);

      if (searchError) {
        debugLogger.error('useConnectionActions', 'Error searching for connections', searchError);
        return false;
      }

      debugLogger.debug('useConnectionActions', 'Found connections to delete', {
        connectionsFound: allConnections?.length || 0,
        connections: allConnections
      });

      if (!allConnections || allConnections.length === 0) {
        debugLogger.debug('useConnectionActions', 'No connections found to delete');
        return true; // Consider it successful if no connection exists
      }

      // Deletar todas as conexões encontradas uma por uma e verificar o resultado
      let deletedCount = 0;
      for (const connection of allConnections) {
        debugLogger.debug('useConnectionActions', 'Deleting connection', {
          connectionId: connection.id,
          userId: connection.user_id,
          targetId: connection.target_id,
          status: connection.status
        });

        const { error: deleteError, count } = await supabase
          .from('connections')
          .delete({ count: 'exact' })
          .eq('id', connection.id);

        if (deleteError) {
          debugLogger.error('useConnectionActions', 'Error deleting specific connection', {
            connectionId: connection.id,
            error: deleteError
          });
          return false;
        }

        debugLogger.debug('useConnectionActions', 'Connection delete result', {
          connectionId: connection.id,
          deletedRows: count
        });

        if (count && count > 0) {
          deletedCount++;
        }
      }

      // Verificar se as conexões foram realmente deletadas
      await new Promise(resolve => setTimeout(resolve, 100)); // Pequeno delay para garantir consistência

      const { data: verificationCheck, error: verifyError } = await supabase
        .from('connections')
        .select('id')
        .or(`and(user_id.eq.${user.id},target_id.eq.${profileId}),and(user_id.eq.${profileId},target_id.eq.${user.id})`);

      if (verifyError) {
        debugLogger.error('useConnectionActions', 'Error verifying deletion', verifyError);
      } else {
        debugLogger.debug('useConnectionActions', 'Post-deletion verification', {
          remainingConnections: verificationCheck?.length || 0,
          remainingData: verificationCheck
        });
      }

      debugLogger.debug('useConnectionActions', 'Unlike action completed', { 
        profileId, 
        userId: user.id,
        totalConnectionsDeleted: deletedCount,
        originalConnectionsFound: allConnections.length
      });

      return deletedCount > 0;
    } catch (error) {
      debugLogger.error('useConnectionActions', 'Unexpected error in unlikeProfile', error);
      return false;
    }
  };

  const handleUnmatch = async (profileId: string): Promise<boolean> => {
    if (!user) {
      debugLogger.error('useConnectionActions', 'User not authenticated for unmatch action');
      return false;
    }

    try {
      debugLogger.debug('useConnectionActions', 'Attempting to unmatch profile', { profileId, userId: user.id });

      // Buscar conexões matched em ambas as direções
      const { data: userToTarget, error: error1 } = await supabase
        .from('connections')
        .select('*')
        .eq('user_id', user.id)
        .eq('target_id', profileId)
        .eq('status', 'matched')
        .maybeSingle();

      const { data: targetToUser, error: error2 } = await supabase
        .from('connections')
        .select('*')
        .eq('user_id', profileId)
        .eq('target_id', user.id)
        .eq('status', 'matched')
        .maybeSingle();

      if (error1 || error2) {
        debugLogger.error('useConnectionActions', 'Error checking existing connections', { error1, error2 });
        return false;
      }

      const matchedConnection = userToTarget || targetToUser;

      if (!matchedConnection) {
        debugLogger.debug('useConnectionActions', 'No matched connection found', { profileId, userId: user.id });
        return false;
      }

      debugLogger.debug('useConnectionActions', 'Found matched connection to unmatch', {
        connectionId: matchedConnection.id,
        originalUserId: matchedConnection.user_id,
        originalTargetId: matchedConnection.target_id,
        initiatedBy: matchedConnection.initiated_by,
        currentUserId: user.id
      });

      // Verificar quem está desfazendo a conexão para determinar ação correta
      const userInitiatedConnection = matchedConnection.initiated_by === user.id;

      if (userInitiatedConnection) {
        // Se o usuário atual iniciou a conexão, deletar completamente
        // Isso fará com que apenas o like da outra pessoa permaneça (se existir)
        const { error: deleteError } = await supabase
          .from('connections')
          .delete()
          .eq('id', matchedConnection.id);

        if (deleteError) {
          debugLogger.error('useConnectionActions', 'Error deleting connection', deleteError);
          return false;
        }

        debugLogger.debug('useConnectionActions', 'Connection deleted - user initiated', {
          connectionId: matchedConnection.id,
          userId: user.id
        });
      } else {
        // Se o usuário atual não iniciou, alterar para 'sent' mantendo o outro como iniciador
        // Isso preserva o like da outra pessoa
        const { error: updateError } = await supabase
          .from('connections')
          .update({ 
            status: 'sent'
            // Preservamos user_id, target_id e initiated_by
          })
          .eq('id', matchedConnection.id);

        if (updateError) {
          debugLogger.error('useConnectionActions', 'Error updating connection status', updateError);
          return false;
        }

        debugLogger.debug('useConnectionActions', 'Connection updated to sent - other user initiated', {
          connectionId: matchedConnection.id,
          preservedInitiator: matchedConnection.initiated_by,
          userId: user.id
        });
      }

      return true;
    } catch (error) {
      debugLogger.error('useConnectionActions', 'Unexpected error in handleUnmatch', error);
      return false;
    }
  };

  const respondToConnection = async (profileId: string, action: 'like' | 'unlike'): Promise<boolean> => {
    if (!user) {
      debugLogger.error('useConnectionActions', 'User not authenticated for respondToConnection action');
      return false;
    }

    try {
      debugLogger.debug('useConnectionActions', 'Attempting to respond to connection', { profileId, userId: user.id, action });

      // Encontrar a conexão enviada (perfil → usuário atual)
      const { data: existingConnection, error: checkError } = await supabase
        .from('connections')
        .select('*')
        .eq('user_id', profileId)
        .eq('target_id', user.id)
        .eq('status', 'sent')
        .maybeSingle();

      if (checkError) {
        debugLogger.error('useConnectionActions', 'Error checking existing connection', checkError);
        return false;
      }

      if (!existingConnection) {
        debugLogger.debug('useConnectionActions', 'No sent connection found', { profileId, userId: user.id });
        return false;
      }

      let updateData = {};

      if (action === 'like') {
        updateData = { status: 'matched' };
      } else if (action === 'unlike') {
        updateData = { status: 'rejected' };
      }

      const { error: updateError } = await supabase
        .from('connections')
        .update(updateData)
        .eq('id', existingConnection.id);

      if (updateError) {
        debugLogger.error('useConnectionActions', 'Error updating connection status', updateError);
        return false;
      }

      debugLogger.debug('useConnectionActions', 'Respond to connection completed successfully', { profileId, userId: user.id, action });
      return true;
    } catch (error) {
      debugLogger.error('useConnectionActions', 'Unexpected error in respondToConnection', error);
      return false;
    }
  };

  return {
    likeProfile,
    unlikeProfile,
    handleUnmatch,
    respondToConnection,
    deleteConnectionById,
    likeLimits
  };
};
